import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useShimmer } from './ShimmerContext';
import {useTranslation} from "react-i18next";


const TutorialContext = createContext(null);

export const TutorialProvider = ({ children }) => {
    const [currentStep, setCurrentStep] = useState(-1);
    const [isActive, setIsActive] = useState(false);
    const { highlightElement, clearShimmer } = useShimmer();
    const hasStarted = useRef(false);
    const isComplete = useRef(false);
    const currentStepRef = useRef(-1);
    const isProcessingAction = useRef(false);
    const { t } = useTranslation();

    const tutorialStates = [
        {
            id: 'welcome',
            text: t('tutorial.welcome'),
            highlight: "header",
            readyAction: "header.ready",
            completeAction: "header.select"
        },
        {
            id: 'evidence',
            text: t('tutorial.evidence'),
            highlight: "evidenceNode",
            completeAction: "evidenceNode.select"
        },
        {
            id: 'model',
            text: t('tutorial.model'),
            highlight: "modelNode",
            completeAction: "modelNode.select"
        },
        {
            id: 'info',
            text: t('tutorial.info'),
            highlight: "infoPanel",
            completeAction: "click"
        },
        {
            id: 'link',
            text: t('tutorial.link'),
            highlight: "link",
            completeAction: "link.select"
        },
        {
            id: 'secondlink',
            text: t('tutorial.secondlink'),
            highlight: "link",
            completeAction: "link.select"
        },
        {
            id: 'save',
            text: t('tutorial.save'),
            highlight: "savechanges",
            completeAction: "click"
        },
        {
            id: 'references',
            text: t('tutorial.references'),
            highlight: "infoPanel",
            completeAction: "click"
        }
    ];

    // Start the tutorial
    const startTutorial = () => {
        // Don't start if the tutorial was completed in this session
        if (!hasStarted.current && !isComplete.current) {
            console.log('Starting tutorial for the first time');
            hasStarted.current = true;
            setCurrentStep(0);
            currentStepRef.current = 0;
            setIsActive(true);
            isProcessingAction.current = false;
        } else {
            console.log('Tutorial already completed or in progress');
        }
    };

    // End the tutorial
    const endTutorial = () => {
        console.log('Ending tutorial');
        clearShimmer();
        setCurrentStep(-1);
        currentStepRef.current = -1;
        setIsActive(false);
        hasStarted.current = false;
        isComplete.current = true; // Mark as complete for this session
        isProcessingAction.current = false;
    };

    // Handle an action being completed
    const completeAction = (action) => {
        if (!isActive || isProcessingAction.current || isComplete.current) return;

        const currentState = tutorialStates[currentStepRef.current];
        if (!currentState) return;

        // Check if this is a ready action for the current step
        if (action === currentState.readyAction) {
            return;
        }

        // Check if this is the completion action for the current step
        if (action === currentState.completeAction) {
            isProcessingAction.current = true;

            // Clear shimmer for current step
            clearShimmer();

            // Advance to next step
            const nextStep = currentStepRef.current + 1;
            if (nextStep < tutorialStates.length) {
                setCurrentStep(nextStep);
                currentStepRef.current = nextStep;
            } else {
                endTutorial();
            }

            // Reset processing flag after a short delay
            setTimeout(() => {
                isProcessingAction.current = false;
            }, 100);
        }
    };

    // Update shimmer highlight whenever step changes
    useEffect(() => {
        if (isActive && currentStep >= 0 && !isComplete.current) {
            const currentState = tutorialStates[currentStep];
            if (currentState?.highlight) {
                highlightElement(currentState.highlight, 0);
            }
        }

        // Cleanup function
        return () => {
            if (!isActive || isComplete.current) {
                clearShimmer();
            }
        };
    }, [tutorialStates, currentStep, isActive, highlightElement, clearShimmer]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearShimmer();
        };
    }, [clearShimmer]);

    const getCurrentStepData = () => {
        if (!isActive || currentStep < 0 || isComplete.current) return null;
        return tutorialStates[currentStep];
    };

    return (
        <TutorialContext.Provider value={{
            isActive,
            currentStep,
            startTutorial,
            endTutorial,
            completeAction,
            getCurrentStepData,
            isComplete: isComplete.current
        }}>
            {children}
        </TutorialContext.Provider>
    );
};

export const useTutorial = () => {
    const context = useContext(TutorialContext);
    if (!context) {
        throw new Error('useTutorial must be used within a TutorialProvider');
    }
    return context;
};

export default TutorialProvider;